import React, { useMemo } from 'react'
import styled from 'styled-components'
import { PieChart, Pie, Cell, Tooltip } from 'recharts'
import { strings } from '@constants'
import { displayAmount } from '@utils'
import { Text } from '@styles'
import { Link } from '@base'
import { paths } from '@routes'

const ChartWrapper = styled.div`
	position: relative;
	width: 300px;
	height: 300px;
`

const Center = styled.div`
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	text-align: center;
`

const CenterLabel = styled.p`
	color: var(--Cool-Gray-70, #4d5358);
	text-align: center;
	font-family: Poppins;
	font-size: ${Text.SMALL};
	font-style: normal;
	font-weight: ${Text.BOLD_500};
	line-height: 1rem; /* 133.333% */
`

const CenterValue = styled.p`
	color: var(--Cool-Gray-100, #0d1016);
	text-align: center;
	font-family: Poppins;
	font-size: ${Text.M_LARGE};
	font-style: normal;
	font-weight: ${Text.BOLD_500};
	line-height: 1.75rem; /* 155.556% */
`

const TooltipBox = styled.div`
	border-radius: 8px;
	padding: 6px 10px;
	font-size: ${Text.MEDIUM};
	background: #fff;
	border: 1px solid rgba(0, 0, 0, 0.08);
	box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
`

function CustomTooltip({ active, payload }) {
	if (active && payload && payload.length) {
		const { name, value, payload: dataPoint } = payload[0]
		const unit = dataPoint?.unit || ''
		return (
			<TooltipBox>
				<div>{name}</div>
				<div>
					<b>
						{value}
						{unit ? `${unit}` : ''}
					</b>
				</div>
			</TooltipBox>
		)
	}
	return null
}

function buildLedgerUrl({ organisationName, employeeName, fromDate, toDate }) {
	const base = `${paths.admin_ledger}?zoho_status=DUE,OVERDUE&transaction_source=INVOICE&transaction_type=DEBIT`
	const dynamic = { organisation: organisationName, manager_name: employeeName === 'ALL' ? '' : employeeName, from: fromDate, to: toDate }
	const query = Object.entries(dynamic)
		.filter(([, v]) => v !== null && v !== undefined && v !== '')
		.map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
		.join('&')
	return query ? `${base}&${query}` : base
}

function DonutChart({ total_amount, data = [], Colors = [], fromDate, toDate, employeeName, organisationName }) {
	// sum values safely (coerce to Number)
	const totalValue = useMemo(() => (Array.isArray(data) ? data.reduce((s, d) => s + (Number(d?.value) || 0), 0) : 0), [data])

	// determine fallback mode (empty or all zeros)
	const isEmpty = !data || data.length === 0 || totalValue <= 0

	// fallback single-slice data when empty (so donut looks full)
	const fallbackData = [{ name: 'No data', value: 1 }]
	const chartData = isEmpty ? fallbackData : data

	// use neutral color for fallback
	const fallbackColors = ['#E5E7EB'] // light gray
	const chartColors = isEmpty ? fallbackColors : Colors

	return (
		<ChartWrapper>
			<PieChart width={300} height={300}>
				<Pie
					data={chartData}
					cx='50%'
					cy='50%'
					innerRadius={90}
					outerRadius={120}
					startAngle={90}
					endAngle={-270}
					dataKey='value'
					// remove paddingAngle for fallback so slice is perfectly continuous
					paddingAngle={isEmpty ? 0 : 2}
					isAnimationActive={!isEmpty}
				>
					{chartData.map((entry, index) => (
						<Cell key={`cell-${index}`} fill={chartColors?.[index] || '#ccc'} />
					))}
				</Pie>

				{/* pass component reference (not an element) */}
				{!isEmpty && <Tooltip content={CustomTooltip} />}
			</PieChart>

			<Center>
				<CenterLabel>{strings('total', 'Receivables')}</CenterLabel>
				<Link
					to={buildLedgerUrl({ organisationName, employeeName, fromDate, toDate })}
					rel='noopener noreferrer'
					target='_blank'
				>
					<CenterValue>{isEmpty ? '-' : `${displayAmount(total_amount)} Cr`}</CenterValue>
				</Link>
			</Center>
		</ChartWrapper>
	)
}

export default DonutChart
